/**
 * ABOUTME: Send view for SmartQuote with adaptive UI based on recipient count
 * ABOUTME: Handles contact info collection and email/SMS sending with platform optimizations
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Phone, CheckCircle, Send, Clipboard, User, 
  ChevronDown, ChevronUp, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tradesperson } from '@/lib/types';
import { toast } from 'sonner';
import { debug } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  validateContactForm, generateMailtoUrl, generateSmsLink, 
  isMobilePlatform, isSafari, openUrl
} from './smartQuoteUtils';

interface SmartQuoteSendViewProps {
  message: string;
  selectedTradespeople: string[];
  tradespeople: Tradesperson[];
  initialName?: string;
  onSendEmail: (contactEmail: string) => Promise<boolean>;
  onSendText: (tradeId: string) => Promise<boolean>;
  onComplete: () => void;
  onBack: () => void;
}

export function SmartQuoteSendView({
  message,
  selectedTradespeople,
  tradespeople,
  initialName = '',
  onSendEmail,
  onSendText,
  onComplete,
  onBack
}: SmartQuoteSendViewProps) {
  // Contact details state - simplified to only name
  const [name, setName] = useState(initialName);
  const [contactError, setContactError] = useState<string | null>(null);

  // Send options state
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [isRecipientListOpen, setIsRecipientListOpen] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<Record<string, boolean>>(
    Object.fromEntries(selectedTradespeople.map(id => [id, true]))
  );
  const [sendingStatus, setSendingStatus] = useState<Record<string, 'pending' | 'sending' | 'sent' | 'error'>>({});
  const [isSendingAll, setIsSendingAll] = useState(false);

  // Get filtered tradespeople that match our selected IDs
  const selectedTradesData = tradespeople.filter(
    trade => selectedTradespeople.includes(trade.id)
  );

  // Filter by contact method
  const emailRecipients = selectedTradesData.filter(trade => trade.email);
  const textRecipients = selectedTradesData.filter(trade => trade.phone);

  // Determine if this is a single recipient case
  const isSingleRecipient = selectedTradesData.length === 1;

  // Get the single recipient if there's only one
  const singleRecipient = isSingleRecipient ? selectedTradesData[0] : null;

  // Check available methods for single recipient
  const hasSingleEmail = singleRecipient?.email ? true : false;
  const hasSinglePhone = singleRecipient?.phone ? true : false;

  // Toggle selection for a tradesperson
  const toggleRecipient = (tradeId: string) => {
    setSelectedRecipients(prev => ({
      ...prev,
      [tradeId]: !prev[tradeId]
    }));
  };

  // Copy the message to clipboard
  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      toast.success('Message copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy message');
      debug.error('Copy message error:', error);
    }
  };

  // Send email using direct DOM approach
  const handleSendEmailToAll = () => {
    const validation = validateContactForm(name);
    if (!validation.isValid) {
      setContactError(validation.errorMessage);
      return;
    }

    try {
      setIsSendingAll(true);

      // Create the mailto URL
      const mailtoUrl = generateMailtoUrl(
        selectedTradesData, 
        selectedRecipients,
        'Quote Request',
        message
      );
      
      if (!mailtoUrl) {
        toast.error('No recipient emails available');
        setIsSendingAll(false);
        return;
      }

      // Open the URL using the direct DOM approach
      const success = openUrl(mailtoUrl);
      if (!success) {
        toast.error('Failed to open email client. Try the "Copy Message" option.');
        setIsSendingAll(false);
        return;
      }

      // Record a successful action in the database
      // This happens independently of whether the email sends
      if (selectedTradespeople.length > 0) {
        onSendEmail("")
          .then(() => {
            // Success registered in database
            debug.log('Email sending action recorded in database');
          })
          .catch(err => {
            // Database error, but email client still launched
            debug.error('Failed to record email action in database:', err);
          });
      }

      // Show success message
      toast.success('Email client opened');

      // For non-mobile devices, we can safely continue the flow
      // Mobile devices will need to manually return and click Done
      if (!isMobilePlatform()) {
        // Allow UI to update before completing
        setTimeout(() => {
          onComplete();
        }, 1000);
      } else {
        setIsSendingAll(false);
      }
    } catch (error) {
      debug.error('Error opening email client:', error);
      toast.error('Failed to open email client. Try the "Copy Message" option.');
      setIsSendingAll(false);
    }
  };

  // Send text using direct DOM approach
  const handleSendText = (tradeId: string) => {
    const validation = validateContactForm(name);
    if (!validation.isValid) {
      setContactError(validation.errorMessage);
      return false;
    }

    // Update status to sending
    setSendingStatus(prev => ({
      ...prev,
      [tradeId]: 'sending'
    }));

    try {
      // Find the trade
      const trade = tradespeople.find(t => t.id === tradeId);
      if (!trade || !trade.phone) {
        setSendingStatus(prev => ({
          ...prev,
          [tradeId]: 'error'
        }));
        toast.error('Phone number not available');
        return false;
      }

      // Generate SMS link
      const smsLink = generateSmsLink(trade.phone, message);
      if (!smsLink) {
        setSendingStatus(prev => ({
          ...prev,
          [tradeId]: 'error'
        }));
        toast.error('Could not create SMS link');
        return false;
      }

      // Open the URL using the direct DOM approach
      const success = openUrl(smsLink);
      if (!success) {
        setSendingStatus(prev => ({
          ...prev,
          [tradeId]: 'error'
        }));
        toast.error('Failed to open SMS app. Try the "Copy Message" option.');
        return false;
      }

      // Record a successful action in the database
      // This happens independently of whether the SMS app opens
      onSendText(tradeId)
        .then(() => {
          // Success registered in database
          debug.log('SMS sending action recorded in database');
        })
        .catch(err => {
          // Database error, but SMS app still launched
          debug.error('Failed to record SMS action in database:', err);
        });

      // Update status
      setSendingStatus(prev => ({
        ...prev,
        [tradeId]: 'sent'
      }));

      // Show success message
      toast.success('SMS app opened');

      // For single recipient on non-mobile devices, complete the flow
      if (isSingleRecipient && !isMobilePlatform()) {
        setTimeout(() => {
          onComplete();
        }, 1000);
      }

      return true;
    } catch (error) {
      debug.error(`Error opening SMS app for trade ${tradeId}:`, error);
      setSendingStatus(prev => ({
        ...prev,
        [tradeId]: 'error'
      }));
      toast.error('Failed to open SMS app. Try the "Copy Message" option.');
      return false;
    }
  };

  // Single Recipient UI Rendering
  const renderSingleRecipientUI = () => {
    if (!singleRecipient) return null;

    return (
      <div className="flex flex-col space-y-4">
        {/* Contact info - just name field */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-700">Your Details</div>

          {/* Name field */}
          <div className="relative">
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setContactError(null);
              }}
              placeholder="Your name"
              className="w-full pl-8 py-3 border border-gray-200 rounded-lg text-base focus:ring-1 focus:ring-purple-400 focus:border-purple-300"
              autoFocus
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <User className="h-4 w-4" />
            </div>
          </div>

          {/* Error message */}
          {contactError && (
            <div className="text-red-500 text-sm py-0.5">
              {contactError}
            </div>
          )}
        </div>

        {/* Collapsible message preview */}
        <Collapsible
          open={isMessageOpen}
          onOpenChange={setIsMessageOpen}
          className="border border-gray-200 rounded-md overflow-hidden bg-gray-50"
        >
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-sm font-medium text-gray-700">Your Message</span>
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyMessage}
                className="text-sm text-purple-600 hover:text-purple-800 h-8 px-2"
              >
                <Clipboard className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {isMessageOpen ? (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
          <CollapsibleContent>
            <div className="p-3 text-sm text-gray-800 border-t border-gray-200 whitespace-pre-wrap max-h-32 overflow-y-auto">
              {message}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Action buttons - large direct send options */}
        <div className="space-y-3 pt-2">
          <div className="text-sm font-medium text-gray-700">
            Send to {singleRecipient.org || singleRecipient.contact}
          </div>

          <div className="flex flex-col space-y-3">
            {/* Email button - only if email available */}
            {hasSingleEmail && (
              <>
                <Button
                  onClick={handleSendEmailToAll}
                  disabled={isSendingAll}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-4 rounded-lg text-base flex justify-center items-center"
                >
                  {isSendingAll ? (
                    <div className="flex items-center">
                      <motion.div
                        className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      />
                      <span className="ml-2">Sending...</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 mr-2" />
                      <span>Send via Email</span>
                    </div>
                  )}
                </Button>

                {/* Fallback copy message option */}
                <Button
                  onClick={handleCopyMessage}
                  variant="outline"
                  className="w-full border-purple-200 text-purple-700 mt-2 py-3 rounded-lg text-sm flex justify-center items-center"
                >
                  <Clipboard className="h-4 w-4 mr-1.5" />
                  <span>Copy Message</span>
                </Button>
              </>
            )}

            {/* Text button - only if phone available */}
            {hasSinglePhone && (
              <Button
                onClick={() => handleSendText(singleRecipient.id)}
                disabled={sendingStatus[singleRecipient.id] === 'sending'}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg text-base flex justify-center items-center"
              >
                {sendingStatus[singleRecipient.id] === 'sending' ? (
                  <div className="flex items-center">
                    <motion.div
                      className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                    />
                    <span className="ml-2">Sending...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 mr-2" />
                    <span>Send via Text</span>
                  </div>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Multiple Recipients UI Rendering
  const renderMultiRecipientUI = () => {
    return (
      <div className="flex flex-col space-y-4">
        {/* Contact info - just name now */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-700">Your Details</div>

          {/* Name field */}
          <div className="relative">
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setContactError(null);
              }}
              placeholder="Your name"
              className="w-full pl-8 py-3 border border-gray-200 rounded-lg text-base focus:ring-1 focus:ring-purple-400 focus:border-purple-300"
              autoFocus
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <User className="h-4 w-4" />
            </div>
          </div>

          {/* Error message */}
          {contactError && (
            <div className="text-red-500 text-sm py-0.5">
              {contactError}
            </div>
          )}
        </div>

        {/* Collapsible message preview */}
        <Collapsible
          open={isMessageOpen}
          onOpenChange={setIsMessageOpen}
          className="border border-gray-200 rounded-md overflow-hidden bg-gray-50"
        >
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-sm font-medium text-gray-700">Your Message</span>
            <div className="flex space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyMessage}
                className="text-sm text-purple-600 hover:text-purple-800 h-8 px-2"
              >
                <Clipboard className="h-4 w-4 mr-1" />
                Copy
              </Button>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  {isMessageOpen ? (
                    <ChevronUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-500" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
          <CollapsibleContent>
            <div className="p-3 text-sm text-gray-800 border-t border-gray-200 whitespace-pre-wrap max-h-32 overflow-y-auto">
              {message}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Send methods section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-700">
                Sending to {selectedTradespeople.length} {selectedTradespeople.length === 1 ? 'tradesperson' : 'tradespeople'}
              </span>
            </div>
          </div>

          {/* Primary Action Buttons - simplified based on available methods */}
          <div className="flex flex-col space-y-3">
            {/* Email All Button - only show if there are email recipients */}
            {emailRecipients.length > 0 && (
              <div className="space-y-2">
                <Button
                  onClick={handleSendEmailToAll}
                  disabled={isSendingAll || Object.values(selectedRecipients).every(v => !v)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-4 rounded-lg text-base flex justify-center items-center"
                >
                  {isSendingAll ? (
                    <div className="flex items-center">
                      <motion.div
                        className="h-4 w-4 rounded-full border-2 border-white border-t-transparent"
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                      />
                      <span className="ml-2">Sending...</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 mr-2" />
                      <span>Send All via Email {emailRecipients.length > 0 && `(${emailRecipients.length})`}</span>
                    </div>
                  )}
                </Button>

                {/* Fallback option to copy message */}
                <Button
                  onClick={handleCopyMessage}
                  variant="outline"
                  className="w-full border-purple-200 text-purple-700 py-3 rounded-lg text-sm flex justify-center items-center"
                >
                  <Clipboard className="h-4 w-4 mr-1.5" />
                  <span>Copy Message</span>
                </Button>
              </div>
            )}

            {/* Text Button - opens collapsible recipients list */}
            {textRecipients.length > 0 && (
              <div className="space-y-2">
                <Collapsible
                  open={isRecipientListOpen}
                  onOpenChange={setIsRecipientListOpen}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-lg text-base flex justify-between items-center"
                    >
                      <div className="flex items-center">
                        <Phone className="h-5 w-5 mr-2" />
                        <span>Send via Text {textRecipients.length > 0 && `(${textRecipients.length})`}</span>
                      </div>
                      <div>
                        {isRecipientListOpen ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </div>
                    </Button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="space-y-0 bg-white">
                      {textRecipients.map(trade => (
                        <div
                          key={trade.id}
                          className={`flex items-center justify-between p-4 hover:bg-gray-50 border-t border-gray-100 first:border-t-0`}
                        >
                          <div className="flex-1">
                            <div className="font-medium text-base">
                              {trade.org || trade.contact}
                            </div>
                            <div className="text-sm text-gray-500">
                              {trade.phone}
                            </div>
                          </div>

                          {/* Send button with large touch target */}
                          <Button
                            size="lg"
                            disabled={sendingStatus[trade.id] === 'sending'}
                            onClick={() => handleSendText(trade.id)}
                            className={`min-h-[44px] min-w-[88px] ${
                              sendingStatus[trade.id] === 'sending'
                                ? 'bg-gray-200'
                                : sendingStatus[trade.id] === 'sent'
                                  ? 'bg-green-500 hover:bg-green-600 text-white'
                                  : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                          >
                            {sendingStatus[trade.id] === 'sending' ? (
                              <motion.div
                                className="h-5 w-5 rounded-full border-2 border-gray-400 border-t-transparent"
                                animate={{ rotate: 360 }}
                                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                              />
                            ) : sendingStatus[trade.id] === 'sent' ? (
                              <div className="flex items-center">
                                <CheckCircle className="h-5 w-5 mr-1" />
                                <span>Sent</span>
                              </div>
                            ) : (
                              <div className="flex items-center">
                                <Send className="h-5 w-5 mr-1" />
                                <span>Text</span>
                              </div>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      key="smart-quote-send-view"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 5 }}
      transition={{ type: "spring", stiffness: 400, damping: 35 }}
      className="space-y-4"
    >
      {/* Render different UIs based on recipient count */}
      {isSingleRecipient ? renderSingleRecipientUI() : renderMultiRecipientUI()}

      {/* Back and Complete buttons */}
      <div className="flex justify-between pt-2">
        <Button
          variant="outline"
          onClick={onBack}
          className="border-gray-200 text-gray-600 min-h-[44px] px-4"
          size="lg"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>

        {/* Complete button - show Done instead of Skip if we're on Safari */}
        {isSafari() ? (
          <Button
            onClick={onComplete}
            variant="default"
            className="bg-green-600 hover:bg-green-700 text-white min-h-[44px] px-4"
            size="lg"
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Done
          </Button>
        ) : (
          <Button
            onClick={onComplete}
            variant="ghost"
            className="text-purple-700 text-sm"
            size="sm"
          >
            Skip
          </Button>
        )}
      </div>
    </motion.div>
  );
}